import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Phone, Video, Calendar, ExternalLink, User } from 'lucide-react';
import { useSDRConnectionDetails, ConnectionDetail } from '@/hooks/useSDRConnectionDetails';
import { useTranslation } from 'react-i18next';

interface SDRConnectionDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sdrName: string;
  sdrUserId: string;
  dateRange?: { from: Date | null; to: Date | null };
}

export function SDRConnectionDetailModal({
  open,
  onOpenChange,
  sdrName,
  sdrUserId,
  dateRange,
}: SDRConnectionDetailModalProps) {
  const { t } = useTranslation();
  const { connections, loading } = useSDRConnectionDetails(
    open ? sdrUserId : null,
    dateRange
  );

  const handleConnectionClick = (connection: ConnectionDetail) => {
    if (connection.dealId) {
      window.open(`/DealDetail?id=${connection.dealId}`, '_blank');
    }
  };

  const formatConnectionDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const getPeriodLabel = () => {
    if (!dateRange) return '';
    if (dateRange.from === null && dateRange.to === null) {
      return t('common.allTime', 'Todos os tempos');
    }
    if (dateRange.from && dateRange.to) {
      return `${format(dateRange.from, 'dd/MM/yyyy')} - ${format(dateRange.to, 'dd/MM/yyyy')}`;
    }
    return '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              {t('calls.connectionsOf', 'Conexões de')} {sdrName}
              <Badge variant="secondary" className="ml-2">
                {connections.length} {t('calls.connections', 'conexões')}
              </Badge>
            </div>
            {getPeriodLabel() && (
              <span className="text-sm font-normal text-muted-foreground">
                {t('common.period', 'Período')}: {getPeriodLabel()}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))}
            </div>
          ) : connections.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {t('calls.noConnectionsFound', 'Nenhuma conexão encontrada neste período')}
            </div>
          ) : (
            <div className="space-y-2">
              {connections.map((conn) => (
                <div
                  key={conn.id}
                  onClick={() => handleConnectionClick(conn)}
                  className={`p-4 rounded-lg border bg-card transition-colors ${
                    conn.dealId 
                      ? 'hover:bg-accent/50 cursor-pointer' 
                      : 'cursor-default'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`p-2 rounded-full ${
                        conn.type === 'video_call' 
                          ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' 
                          : 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {conn.type === 'video_call' ? (
                          <Video className="h-4 w-4" />
                        ) : (
                          <Phone className="h-4 w-4" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex flex-col gap-1">
                        {/* Type badge */}
                        <Badge 
                          variant="outline" 
                          className={`w-fit text-xs ${
                            conn.type === 'video_call'
                              ? 'border-green-200 text-green-700 dark:border-green-800 dark:text-green-400'
                              : 'border-blue-200 text-blue-700 dark:border-blue-800 dark:text-blue-400'
                          }`}
                        >
                          {conn.type === 'video_call' 
                            ? t('calls.videoCallConfirmed', 'Video Call Confirmada')
                            : t('calls.callTransfer', 'Transferência de Ligação')
                          }
                        </Badge>

                        {/* Contact name */}
                        <span className="font-medium text-foreground">
                          {conn.contactName}
                        </span>

                        {/* Date */}
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {formatConnectionDate(conn.date)}
                        </div>

                        {/* Deal */}
                        {conn.dealName && (
                          <div className="flex items-center gap-1 text-sm text-primary">
                            <span className="text-muted-foreground">Deal:</span>
                            {conn.dealName}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* External link indicator */}
                    {conn.dealId && (
                      <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
